import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that manages cart drawer behavior within a `<theme-drawer>`.
 *
 * Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
 * The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
 * `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
 * Cart count announcements are owned by `<header-actions>`.
 * This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
 * sticky summary layout, and the installments CTA close-on-click.
 *
 * @extends {Component}
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (this.closest('theme-drawer'));
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.addEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    document.addEventListener('positv:cart-add-sections', this.#handleEarlySections);

    // The restore path sets [open] before this module loads, so the
    // theme-drawer:open event will have already fired. Use the attribute
    // check so this works even before <theme-drawer> upgrades.
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
    this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
    this.removeEventListener(ThemeEvents.cartSectionRestored, this.#handleCartSectionRestored);
    document.removeEventListener('positv:cart-add-sections', this.#handleEarlySections);
  }

  /**
   * Positv: shows the updated drawer as soon as the add-to-cart response arrives
   * (the theme otherwise waits for an extra /cart.json round trip first).
   * @param {Event} event
   */
  #handleEarlySections = (event) => {
    const sections = /** @type {CustomEvent} */ (event).detail?.sections;
    const items = this.querySelector('cart-items-component');
    const sectionId = items instanceof HTMLElement ? items.dataset.sectionId : undefined;
    const html = sectionId ? sections?.[sectionId] : undefined;
    if (!sectionId || !html) return;

    const wasEmpty = items?.querySelector('[data-cart-drawer-empty]') !== null;
    try {
      sectionRenderer.abortRender(sectionId);
      Promise.resolve(morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: wasEmpty }))
        .then(() => {
          this.#clearAllButtonsLoading();
          if (this.#pendingOpen) {
            this.#pendingOpen = false;
            if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          }
          requestAnimationFrame(() => this.#updateStickyState());
        })
        .catch(() => {});
    } catch (error) {
      console.warn('[cart-drawer] early render failed:', error);
    }
  };

  /** @type {boolean} */
  #pendingOpen = false;

  /**
   * Positv: puts a spinner inside the add to cart button(s) that triggered the add.
   * @param {Event} event
   * @param {boolean} loading
   */
  #setButtonsLoading(event, loading) {
    if (!loading) {
      this.#clearAllButtonsLoading();
      return;
    }
    const source = event.target instanceof Element ? event.target : null;
    const form = source?.closest('product-form-component') || source;
    /** @type {HTMLElement[]} */
    const buttons = [];
    form?.querySelectorAll('button[name="add"], button.positv-atc').forEach((b) => buttons.push(/** @type {HTMLElement} */ (b)));
    // The sticky bar clicks the main button, so it gets the spinner too.
    if (form?.querySelector('.positv-atc')) {
      document.querySelectorAll('[data-lv-sticky-cta]').forEach((b) => buttons.push(/** @type {HTMLElement} */ (b)));
    }
    buttons.forEach((button) => {
      if (button.hasAttribute('data-positv-loading')) return;
      button.style.setProperty('--positv-spin', getComputedStyle(button).color);
      button.setAttribute('data-positv-loading', '');
    });
  }

  #clearAllButtonsLoading() {
    document.querySelectorAll('[data-positv-loading]').forEach((el) => el.removeAttribute('data-positv-loading'));
  }

  /**
   * Recomputes the sticky summary after a back/forward cache restore re-renders the cart items.
   *
   * On viewports at or above 990px an open drawer survives a navigation and is reopened during
   * parsing (see the restore script in `snippets/theme-drawer.liquid`), so no open event fires
   * on the way back and the measurement taken before the shopper left is the one still applied.
   * An empty drawer measured as unpinned would keep the checkout CTA scrolling away once the
   * restore fills it with line items.
   */
  #handleCartSectionRestored = () => {
    requestAnimationFrame(() => this.#updateStickyState());
  };

  /**
   * Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    // Close cart drawer when installments CTA is clicked to avoid overlapping dialogs.
    // Re-queried on every open so it survives cart content re-renders that
    // replace the shopify-payment-terms shadow root.
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta = this.querySelector('shopify-payment-terms')?.shadowRoot?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', () => this.#themeDrawer?.close(), { once: true });
    });
  };

  /**
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen = this.hasAttribute('auto-open') && event.action === 'add' && !this.#themeDrawer?.isOpen;

    // When the event originates inside an open MODAL <dialog> (e.g. quick-add),
    // defer the auto-open until that dialog's native `close` fires so its focus
    // restoration runs first — otherwise we'd capture the wrong
    // `#previouslyFocused`. Non-modal dialogs (e.g. the hotspot preview) don't
    // close on add and don't move focus, so `:modal` excludes them.
    const sourceModal = /** @type {HTMLDialogElement | null} */ (
      event.target instanceof Element ? event.target.closest('dialog:modal') : null
    );

    // Positv: no early open. The add to cart button shows a spinner while the item is
    // being added; the drawer opens as soon as the updated cart is ready (#handleEarlySections).
    if (event.action === 'add') {
      this.#pendingOpen = shouldAutoOpen && !sourceModal;
      this.#setButtonsLoading(event, true);
      const done = () => this.#setButtonsLoading(event, false);
      event.promise?.then(done, done);
      window.setTimeout(done, 8000);
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () => requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          this.#pendingOpen = false;
          if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener('close', openAndSettle, { once: true });
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-drawer] Event promise rejected:', error);
      });
  };

  #isCartEmpty() {
    return Boolean(this.querySelector('.cart-drawer--empty'));
  }

  #updateStickyState() {
    const dialog = this.#dialog;
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
